/**
 * usePrinter — React hook for amzyticks POS web app integration
 *
 * Connects to the amzyticks POS Printer companion app via WebSocket.
 * Drop this file into your amzyticks-pos-web project and import it.
 *
 * Usage:
 *   import { usePrinter } from './hooks/usePrinter';
 *
 *   function MyComponent() {
 *     const { printReceipt, printLabel, printRegular, getPrinters, status } = usePrinter();
 *
 *     const handlePrint = async () => {
 *       await printReceipt({ lines: [{ type: 'text', text: 'Hello!' }, { type: 'cut' }] });
 *     };
 *   }
 *
 * Compatible with: React 16.8+ (hooks), plain JavaScript (remove types if needed)
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// ─── Config ───────────────────────────────────────────────────────────────────

const DEFAULT_PORT = 12212;
const DEFAULT_TLS_PORT = 12213;
const RECONNECT_DELAY_MS = 3000;
const MAX_RECONNECT_ATTEMPTS = 10;
const PING_INTERVAL_MS = 25000;
const REQUEST_TIMEOUT_MS = 30000;

async function hmacSha256Hex(secret, text) {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const signature = await crypto.subtle.sign('HMAC', key, enc.encode(text));
  return Array.from(new Uint8Array(signature)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function sortValue(value) {
  if (Array.isArray(value)) return value.map(sortValue);
  if (value && typeof value === 'object') {
    return Object.keys(value).sort().reduce((acc, key) => {
      acc[key] = sortValue(value[key]);
      return acc;
    }, {});
  }
  return value;
}

export async function createSignedEnvelope(payload, { secret, origin, nonce = `${Date.now()}_${Math.random().toString(36).slice(2)}` } = {}) {
  const timestamp = Date.now();
  const canonical = JSON.stringify(sortValue({
    origin,
    timestamp,
    nonce,
    payload,
  }));
  const value = await hmacSha256Hex(secret, canonical);
  return {
    ...payload,
    signature: {
      algorithm: 'hmac-sha256',
      timestamp,
      nonce,
      value,
    },
  };
}

// Detect the companion app port (same for localhost in all cases)
function getWsUrls({ port = DEFAULT_PORT, tlsPort = DEFAULT_TLS_PORT } = {}) {
  const secure = `wss://localhost:${tlsPort}`;
  const insecure = `ws://localhost:${port}`;
  const hostname = typeof window !== 'undefined' ? window.location?.hostname : '';
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';

  // HTTPS pages cannot connect to ws:// (mixed content). Prefer wss:// only.
  if (typeof window !== 'undefined' && window.location?.protocol === 'https:') {
    return [secure];
  }

  // Local HTTP development should hit the plain WebSocket endpoint first.
  if (isLocalhost) {
    return [insecure, secure];
  }

  // For non-local HTTP, prefer secure first and then fall back.
  return [secure, insecure];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * @param {Object} options
 * @param {number}  [options.port=12212]          WebSocket port
 * @param {boolean} [options.autoConnect=true]    Connect on mount
 * @param {Function} [options.onConnect]          Called when connected
 * @param {Function} [options.onDisconnect]       Called when disconnected
 * @param {Function} [options.onPrintResult]      Called with each print result
 */
const UNSIGNED_TYPES = new Set(['ping', 'handshake', 'get_security_status', 'get_queue_status', 'get_pricing', 'register_client']);

export function usePrinter({
  port = DEFAULT_PORT,
  tlsPort = DEFAULT_TLS_PORT,
  autoConnect = true,
  secret,
  origin,
  onConnect,
  onDisconnect,
  onPrintResult,
} = {}) {
  const [status, setStatus] = useState('disconnected'); // 'connecting'|'connected'|'disconnected'|'error'
  const [printers, setPrinters] = useState(null);
  const [appVersion, setAppVersion] = useState(null);
  const [security, setSecurity] = useState(null);
  const [pricing, setPricing] = useState(null);

  const wsRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  const pendingRequestsRef = useRef(new Map()); // jobId → { resolve, reject, timer }
  const unmountedRef = useRef(false);

  // ─── Connect ──────────────────────────────────────────────────────────────

  const connect = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
    if (unmountedRef.current) return;

    const urls = getWsUrls({ port, tlsPort });
    setStatus('connecting');

    const tryUrl = (index = 0) => {
      const url = urls[index];
      if (!url) {
        setStatus('error');
        scheduleReconnect();
        return;
      }

      let ws;
      try {
        ws = new WebSocket(url);
      } catch (err) {
        console.error('[usePrinter] WebSocket construction failed:', err);
        tryUrl(index + 1);
        return;
      }

      wsRef.current = ws;

      const fallbackTimer = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          try { ws.close(); } catch {}
          tryUrl(index + 1);
        }
      }, 1500);

      ws.onopen = () => {
        clearTimeout(fallbackTimer);
        if (unmountedRef.current) return;
        console.log('[usePrinter] Connected to companion app');
        reconnectAttemptsRef.current = 0;
        setStatus('connected');
        startPingInterval();
        onConnect?.();
      };

      ws.onmessage = (event) => {
        if (unmountedRef.current) return;
        let msg;
        try {
          msg = JSON.parse(event.data);
        } catch {
          return;
        }
        handleMessage(msg);
      };

      ws.onerror = () => {
        clearTimeout(fallbackTimer);
        if (unmountedRef.current) return;
        tryUrl(index + 1);
      };

      ws.onclose = (event) => {
        clearTimeout(fallbackTimer);
        if (unmountedRef.current) return;
        console.log(`[usePrinter] Disconnected (code=${event.code})`);
        setStatus('disconnected');
        stopPingInterval();
        rejectAllPending('Connection closed');
        onDisconnect?.();
        scheduleReconnect();
      };
    };

    tryUrl(0);
  }, [port, tlsPort]);

  // ─── Disconnect ───────────────────────────────────────────────────────────

  const disconnect = useCallback(() => {
    clearTimeout(reconnectTimerRef.current);
    stopPingInterval();
    if (wsRef.current) {
      wsRef.current.onclose = null; // prevent reconnect on manual close
      wsRef.current.close();
      wsRef.current = null;
    }
    setStatus('disconnected');
  }, []);

  // ─── Message router ───────────────────────────────────────────────────────

  function handleMessage(msg) {
    switch (msg.type) {
      case 'ready':
        setAppVersion(msg.version);
        if (msg.trust) setSecurity(msg.trust);
        break;

      case 'pong':
        // heartbeat acknowledged
        break;

      case 'handshake':
        setSecurity(msg.data);
        resolveRequest('handshake', msg.data);
        break;

      case 'pricing':
        setPricing(msg.data);
        resolveRequest('pricing', msg.data);
        break;

      case 'client_registered':
        resolveRequest('client_registered', msg.data);
        break;

      case 'license_required':
        setPricing({ plans: msg.pricing || [] });
        rejectAllPending(msg.error || 'License required');
        break;

      case 'printers':
        setPrinters(msg.data);
        // Resolve pending getPrinters request
        resolveRequest('get_printers', msg.data);
        break;

      case 'print_result':
        onPrintResult?.(msg);
        // Resolve or reject the pending print promise
        if (msg.success) {
          resolveRequest(msg.job_id, msg);
        } else {
          rejectRequest(msg.job_id, new Error(msg.error || 'Print failed'));
        }
        break;

      case 'error':
        console.error('[usePrinter] Server error:', msg.error);
        // Reject all pending requests so they surface immediately instead of timing out
        rejectAllPending(msg.error || 'Server error');
        break;

      default:
        break;
    }
  }

  // ─── Send helpers ─────────────────────────────────────────────────────────

  function send(payload) {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      throw new Error('Not connected to amzyticks POS Printer companion app. Is it running?');
    }
    wsRef.current.send(JSON.stringify(payload));
  }

  /**
   * Send a message and return a promise that resolves when the response arrives.
   * @param {Object} payload - Message to send
   * @param {string} requestKey - Key to match response (job_id or 'get_printers')
   */
  function sendAndWait(payload, requestKey) {
    return new Promise(async (resolve, reject) => {
      let envelope = payload;
      if (secret && !UNSIGNED_TYPES.has(payload.type)) {
        try {
          envelope = await createSignedEnvelope(payload, { secret, origin: origin || window.location.origin });
        } catch (e) {
          return reject(new Error('Failed to sign request: ' + e.message));
        }
      }
      try { send(envelope); } catch (e) { return reject(e); }

      const timer = setTimeout(() => {
        pendingRequestsRef.current.delete(requestKey);
        reject(new Error(`Request timed out (${requestKey})`));
      }, REQUEST_TIMEOUT_MS);

      pendingRequestsRef.current.set(requestKey, { resolve, reject, timer });
    });
  }

  function resolveRequest(key, data) {
    const pending = pendingRequestsRef.current.get(key);
    if (pending) {
      clearTimeout(pending.timer);
      pending.resolve(data);
      pendingRequestsRef.current.delete(key);
    }
  }

  function rejectRequest(key, err) {
    const pending = pendingRequestsRef.current.get(key);
    if (pending) {
      clearTimeout(pending.timer);
      pending.reject(err);
      pendingRequestsRef.current.delete(key);
    }
  }

  function rejectAllPending(reason) {
    pendingRequestsRef.current.forEach(({ timer, reject }) => {
      clearTimeout(timer);
      reject(new Error(reason));
    });
    pendingRequestsRef.current.clear();
  }

  // ─── Heartbeat ────────────────────────────────────────────────────────────

  function startPingInterval() {
    pingTimerRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, PING_INTERVAL_MS);
  }

  function stopPingInterval() {
    clearInterval(pingTimerRef.current);
  }

  // ─── Reconnect ────────────────────────────────────────────────────────────

  function scheduleReconnect() {
    if (unmountedRef.current) return;
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      console.warn('[usePrinter] Max reconnect attempts reached');
      setStatus('error');
      return;
    }
    reconnectAttemptsRef.current++;
    const delay = Math.min(RECONNECT_DELAY_MS * reconnectAttemptsRef.current, 30000);
    console.log(`[usePrinter] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
    reconnectTimerRef.current = setTimeout(connect, delay);
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  useEffect(() => {
    unmountedRef.current = false;
    if (autoConnect) connect();

    return () => {
      unmountedRef.current = true;
      disconnect();
    };
  }, [port]);

  // ─── Public API ───────────────────────────────────────────────────────────

  /**
   * Print a receipt on the configured thermal printer.
   *
   * @param {Object} data - Receipt data
   * @param {Array}  data.lines - Array of print line objects
   * @param {string} [printerName] - Override printer name
   * @param {string} [connection] - Override connection type
   * @param {Object} [connectionDetails] - Override connection details { host, port }
   * @param {number} [copies=1]
   */
  const printReceipt = useCallback(async (data, { printerName, connection, connectionDetails, copies = 1 } = {}) => {
    const jobId = `receipt_${Date.now()}`;
    return sendAndWait(
      {
        type: 'print',
        job_id: jobId,
        printer_type: 'receipt',
        protocol: 'escpos',
        ...(printerName && { printer_name: printerName }),
        ...(connection && { connection }),
        ...(connectionDetails && { connection_details: connectionDetails }),
        copies,
        data,
      },
      jobId
    );
  }, []);

  /**
   * Print a label using TSPL or ZPL.
   *
   * @param {Object} data - Label data with elements array
   * @param {string} [protocol='tspl'] - 'tspl' | 'zpl'
   * @param {string} [printerName]
   * @param {string} [connection]
   * @param {Object} [connectionDetails]
   * @param {number} [copies=1]
   */
  const printLabel = useCallback(async (data, { protocol = 'tspl', printerName, connection, connectionDetails, copies = 1 } = {}) => {
    const jobId = `label_${Date.now()}`;
    return sendAndWait(
      {
        type: 'print',
        job_id: jobId,
        printer_type: 'label',
        protocol,
        ...(printerName && { printer_name: printerName }),
        ...(connection && { connection }),
        ...(connectionDetails && { connection_details: connectionDetails }),
        copies,
        data,
      },
      jobId
    );
  }, []);

  /**
   * Print HTML content on a regular A4/Letter printer.
   *
   * @param {Object} data
   * @param {string} data.html        - HTML content string
   * @param {string} [data.css]       - Additional CSS
   * @param {boolean} [data.landscape]
   * @param {string} [printerName]
   * @param {string} [paperSize='A4']
   * @param {number} [copies=1]
   */
  const printRegular = useCallback(async (data, { printerName, paperSize = 'A4', copies = 1 } = {}) => {
    const jobId = `regular_${Date.now()}`;
    return sendAndWait(
      {
        type: 'print',
        job_id: jobId,
        printer_type: 'regular',
        ...(printerName && { printer_name: printerName }),
        copies,
        data,
      },
      jobId
    );
  }, []);

  /**
   * Fetch all printers detected by the companion app.
   * Returns { osPrinters, usbPrinters, networkPrinters }
   */
  const getPrinters = useCallback(async () => {
    return sendAndWait({ type: 'get_printers' }, 'get_printers');
  }, []);

  const getHandshake = useCallback(async () => {
    return sendAndWait({ type: 'handshake' }, 'handshake');
  }, []);

  const registerClient = useCallback(async ({ label, mode = 'signed' } = {}) => {
    return sendAndWait({ type: 'register_client', label, mode }, 'client_registered');
  }, []);

  const getPricing = useCallback(async () => {
    return sendAndWait({ type: 'get_pricing' }, 'pricing');
  }, []);

  /**
   * Open the cash drawer via the receipt printer.
   */
  const openCashDrawer = useCallback(async ({ printerName, connection, connectionDetails } = {}) => {
    return printReceipt(
      { lines: [{ type: 'cashdrawer' }] },
      { printerName, connection, connectionDetails, copies: 1 }
    );
  }, [printReceipt]);

  return {
    /** 'connecting' | 'connected' | 'disconnected' | 'error' */
    status,
    /** true when status === 'connected' */
    isConnected: status === 'connected',
    /** Companion app version string */
    appVersion,
    security,
    pricing,
    /** Cached printer list from last getPrinters() call */
    printers,

    // Actions
    printReceipt,
    printLabel,
    printRegular,
    getPrinters,
    getHandshake,
    registerClient,
    getPricing,
    openCashDrawer,

    // Connection control
    connect,
    disconnect,
  };
}

// ─── Helper: build a receipt from a simple order object ─────────────────────

/**
 * Build a receipt lines array from an order object.
 * Convenience function for the amzyticks POS billing flow.
 *
 * @param {Object} order
 * @param {string} order.storeName
 * @param {Array}  order.items     - [{ name, qty, price }]
 * @param {number} order.subtotal
 * @param {number} order.tax
 * @param {number} order.total
 * @param {string} [order.paymentMethod]
 * @param {string} [order.invoiceNo]
 * @param {string} [order.footerText]
 */
export function buildReceiptLines(order) {
  const lines = [];

  // Store name
  lines.push({ type: 'text', text: order.storeName || 'amzyticks POS', align: 'center', bold: true, fontSize: 2 });
  lines.push({ type: 'separator' });

  // Invoice & date
  if (order.invoiceNo) {
    lines.push({ type: 'text', text: `Invoice: ${order.invoiceNo}` });
  }
  lines.push({ type: 'text', text: `Date: ${new Date().toLocaleString()}` });
  lines.push({ type: 'separator' });

  // Items
  for (const item of order.items || []) {
    const priceStr = `₹${Number(item.price).toFixed(2)}`;
    const nameWithQty = `${item.qty}x ${item.name}`;
    const padding = ' '.repeat(Math.max(1, 32 - nameWithQty.length - priceStr.length));
    lines.push({ type: 'text', text: `${nameWithQty}${padding}${priceStr}` });
  }

  lines.push({ type: 'separator' });

  // Totals
  lines.push({ type: 'text', text: `Subtotal:          ₹${Number(order.subtotal).toFixed(2)}` });
  if (order.tax) {
    lines.push({ type: 'text', text: `Tax:               ₹${Number(order.tax).toFixed(2)}` });
  }
  lines.push({ type: 'text', text: `TOTAL:             ₹${Number(order.total).toFixed(2)}`, bold: true });

  if (order.paymentMethod) {
    lines.push({ type: 'text', text: `Paid via: ${order.paymentMethod}` });
  }

  lines.push({ type: 'separator' });
  lines.push({ type: 'text', text: order.footerText || 'Thank you for your purchase!', align: 'center' });
  lines.push({ type: 'newline', count: 2 });
  lines.push({ type: 'cut' });

  return lines;
}

/**
 * Build a barcode label data object from a product.
 * For use with printLabel().
 *
 * @param {Object} product
 * @param {string} product.name
 * @param {string} product.sku
 * @param {number} product.price
 * @param {number} [labelWidth=80]
 * @param {number} [labelHeight=40]
 */
export function buildBarcodeLabel(product, labelWidth = 80, labelHeight = 40) {
  return {
    labelWidth,
    labelHeight,
    gap: 2,
    elements: [
      {
        type: 'text',
        x: 20,
        y: 20,
        font: '3',
        xMultiply: 1,
        yMultiply: 1,
        text: (product.name || '').slice(0, 24),
      },
      {
        type: 'text',
        x: 20,
        y: 55,
        font: '2',
        xMultiply: 1,
        yMultiply: 1,
        text: `₹${Number(product.price).toFixed(2)}`,
      },
      {
        type: 'barcode',
        x: 20,
        y: 85,
        barcodeType: 'CODE128',
        height: 70,
        readable: 1,
        data: product.sku || product.id || 'UNKNOWN',
      },
    ],
  };
}

const PRODUCT_LABEL_SPECS = {
  small:  { width: 40,  height: 20, nameLimit: 18, priceY: 36, barcodeY: 62, barcodeHeight: 28, font: '1' },
  medium: { width: 58,  height: 40, nameLimit: 22, priceY: 58, barcodeY: 92, barcodeHeight: 46, font: '2' },
  large:  { width: 100, height: 60, nameLimit: 28, priceY: 72, barcodeY: 118, barcodeHeight: 68, font: '3' },
};

export function buildProductBarcodeLabel(product, { labelSize = 'medium', orgName = '', labelType = 'barcode' } = {}) {
  const spec = PRODUCT_LABEL_SPECS[labelSize] || PRODUCT_LABEL_SPECS.medium;
  const barcodeValue = product.custom_barcode || product.sku || product.id || 'UNKNOWN';
  const priceValue = product.retail_price;
  const hasPrice = priceValue !== undefined && priceValue !== null && priceValue !== '';
  const isQR = labelType === 'qrcode';
  const elements = [];

  if (orgName) {
    elements.push({
      type: 'text',
      x: 12,
      y: 10,
      font: '1',
      xMultiply: 1,
      yMultiply: 1,
      text: String(orgName).slice(0, spec.nameLimit),
    });
  }

  elements.push({
    type: 'text',
    x: 12,
    y: orgName ? 28 : 16,
    font: spec.font,
    xMultiply: 1,
    yMultiply: 1,
    text: String(product.name || '').slice(0, spec.nameLimit),
  });

  if (hasPrice) {
    elements.push({
      type: 'text',
      x: 12,
      y: spec.priceY,
      font: spec.font,
      xMultiply: 1,
      yMultiply: 1,
      text: `Rs ${Number(priceValue).toFixed(2)}`,
    });
  }

  if (isQR) {
    // QR code element — `size` is the universal field accepted by both TSPL (cellWidth) and ZPL (magnification).
    // Scale with label size so the QR isn't too small to scan on a 40mm label.
    const size = spec.width <= 40 ? 3 : spec.width <= 58 ? 4 : 6;
    elements.push({
      type: 'qrcode',
      x: 12,
      y: spec.barcodeY,
      ecLevel: 'M',
      size,
      mode: 'A',
      data: String(barcodeValue),
    });
  } else {
    elements.push({
      type: 'barcode',
      x: 12,
      y: spec.barcodeY,
      barcodeType: 'CODE128',
      height: spec.barcodeHeight,
      readable: 1,
      data: String(barcodeValue),
    });
  }

  return {
    labelWidth: spec.width,
    labelHeight: spec.height,
    gap: 2,
    elements,
  };
}
